import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Mail } from 'lucide-react';

interface InviteVendorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (email: string, message: string) => Promise<void>;
  checkExistingVendor: (email: string) => Promise<boolean>;
  dailyCount: number;
}

export const InviteVendorDialog: React.FC<InviteVendorDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  checkExistingVendor,
  dailyCount,
}) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExistingVendor, setIsExistingVendor] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const maxChars = 500;
  const remainingChars = maxChars - message.length;
  const isOverLimit = remainingChars < 0;
  const dailyLimitReached = dailyCount >= 10;

  useEffect(() => {
    if (!open) {
      setEmail('');
      setMessage('');
      setIsExistingVendor(false);
    }
  }, [open]);

  const handleEmailBlur = async () => {
    if (!email || !email.includes('@')) return;

    setIsCheckingEmail(true);
    try {
      const exists = await checkExistingVendor(email);
      setIsExistingVendor(exists);
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleSubmit = async () => {
    if (!email || isOverLimit || isExistingVendor || dailyLimitReached) return;

    setIsSubmitting(true);
    try {
      await onSubmit(email, message);
      onOpenChange(false);
    } catch (error) {
      // Error handled by hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Vendor to Platform</DialogTitle>
          <DialogDescription>
            Send an invitation to a vendor to join the platform and participate in RFQs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {dailyLimitReached && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                You've reached your daily invitation limit (10/10). Please try again tomorrow.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Vendor Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="vendor@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                disabled={isSubmitting || dailyLimitReached}
                className="pl-9"
              />
            </div>
            {isCheckingEmail && (
              <p className="text-xs text-muted-foreground">Checking email...</p>
            )}
            {isExistingVendor && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This email is already registered as a vendor on the platform.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Personal Message (Optional)</Label>
            <Textarea
              id="message"
              placeholder="Add a personal note to your invitation..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting || dailyLimitReached}
              rows={4}
              className={isOverLimit ? 'border-destructive' : ''}
            />
            <p className={`text-xs ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
              {remainingChars} characters remaining
            </p>
          </div>

          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">
              Daily invitations: {dailyCount}/10
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !email ||
                  isOverLimit ||
                  isExistingVendor ||
                  isSubmitting ||
                  dailyLimitReached
                }
              >
                {isSubmitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
